import React from 'react';
import '../styles/AboutUsSection.css';
import CompanyProfile from './CompanyProfile';

// Custom SVG Icons
const CheckCircle = ({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M9 12l2 2 4-4"/>
    <circle cx="12" cy="12" r="10"/>
  </svg>
);

const Users = ({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <path d="m22 21-2-2"/>
    <path d="m16 16 2 2"/>
    <circle cx="20" cy="8" r="3"/>
  </svg>
);

const Clock = ({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10"/>
    <polyline points="12,6 12,12 16,14"/>
  </svg>
);

const Award = ({ size = 24, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="8" r="6"/>
    <path d="m9 12 2 2 4-4"/>
    <path d="m8 18 4-4 4 4"/>
    <path d="m8 22 4-4 4 4"/>
  </svg>
);



const ChefHat = ({ size = 48, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M6 13.87A4 4 0 0 1 7.41 6a5.11 5.11 0 0 1 1.05-1.54 5 5 0 0 1 7.08 0A5.11 5.11 0 0 1 16.59 6 4 4 0 0 1 18 13.87V21H6Z"/>
    <line x1="6" y1="17" x2="18" y2="17"/>
  </svg>
);

const Building = ({ size = 32, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="16" height="20" x="4" y="2" rx="2" ry="2"/>
    <path d="M9 22v-4h6v4"/>
    <path d="M8 6h.01"/>
    <path d="M16 6h.01"/>
    <path d="M12 6h.01"/>
    <path d="M12 10h.01"/>
    <path d="M12 14h.01"/>
    <path d="M16 10h.01"/>
    <path d="M16 14h.01"/>
    <path d="M8 10h.01"/>
    <path d="M8 14h.01"/>
  </svg>
);

const AboutUsSection = () => {
  const laxmiFeatures = [
    {
      icon: <Users size={32} className="laxmi-feature-icon" />,
      title: "Scalable Solutions",
      description: "Adaptable for any workforce size or shift structure with flexible meal planning and service delivery"
    },
    {
      icon: <Clock size={32} className="laxmi-feature-icon" />,
      title: "Punctual & Hygienic",
      description: "Consistent service delivery at every meal interval with strict hygiene and safety standards"
    },
    {
      icon: <Award size={32} className="laxmi-feature-icon" />,
      title: "Quality Assured",
      description: "Maintaining consistent taste, quality, and nutrition standards across all our service locations"
    }
  ];

  const laxmiHighlights = [
    "Customized menus reflecting regional tastes and preferences",
    "Comprehensive nutritional planning for optimal workforce energy",
    "Seamless coordination with complex shift patterns and schedules",
    "Clean, comfortable dining environments that enhance employee satisfaction",
    "Professionally trained and uniformed service teams",
    "Strict safety protocols and accountability measures"
  ];

  return (
    <div className="laxmi-about-container">
      {/* Hero Section */}
      <section className="laxmi-hero-section">
        <div className="laxmi-hero-content">
          <div className="laxmi-hero-text">
            <h1 className="laxmi-hero-title">
              Precision-Fed Productivity
              <span className="laxmi-hero-subtitle">Since 2009</span>
            </h1>
            <p className="laxmi-hero-description">
              At Laxmi Hospitality Service, we believe a well-fed workforce is a productive one. 
              Our comprehensive industrial canteen solutions ensure your team receives nutritious, 
              timely meals that fuel productivity and workplace satisfaction.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <main className="laxmi-main-content">
        {/* Journey Section */}
        <section className="laxmi-journey-section">
          <div className="laxmi-journey-grid">
            <div className="laxmi-journey-text">
              <h2 className="laxmi-section-title">Our Journey Since 2009</h2>
              <p className="laxmi-section-text">
                Since our humble inception in 2009, we've grown into one of India's most trusted names in industrial canteen management, delivering culinary excellence to factories, mills, manufacturing plants, and  industrial hubs across the region.
              </p>
              <p className="laxmi-section-text">
                From day one, our mission has been clear: to redefine canteen hospitality with uncompromising quality, punctuality, and trust. Every service we provide is executed with meticulous care and commitment to operational excellence.
              </p>
              <p className="laxmi-section-text">
                Our expertise spans diverse industrial environments, from heavy manufacturing to industrial campuses, ensuring seamless food service operations that complement your business objectives.
              </p>
            </div>
            
            <div className="laxmi-anniversary-wrapper">
              <div className="laxmi-anniversary-card">
                <div className="laxmi-anniversary-inner">
                  <ChefHat size={64} className="laxmi-anniversary-icon" />
                  <div className="laxmi-anniversary-content">
                    <h3 className="laxmi-anniversary-number">15+</h3>
                    <p className="laxmi-anniversary-text">Years of Excellence</p>
                    <span className="laxmi-anniversary-subtext">in Industrial Catering</span>
                  </div>
                </div>
                <div className="laxmi-stats-grid">
                  <div className="laxmi-stat-item">
                    <span className="laxmi-stat-number">50+</span>
                    <span className="laxmi-stat-label">Happy Clients</span>
                  </div>
                  <div className="laxmi-stat-item">
                    <span className="laxmi-stat-number">1M+</span>
                    <span className="laxmi-stat-label">Meals Served</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <CompanyProfile />


        {/* Services Section */}
        <section className="laxmi-services-section">
          <div className="laxmi-services-card">
            <div className="laxmi-services-header">
              <h2 className="laxmi-services-title">
                Industrial Canteen Services That Work Harder Than Your Machinery
              </h2>
              <p className="laxmi-services-description">
                We specialize in comprehensive canteen solutions, precisely tailored to match the operational pulse of your industrial facility, ensuring seamless integration with your workflow.
              </p>
            </div>

            <div className="laxmi-features-grid">
              {laxmiFeatures.map((feature, index) => (
                <div key={index} className="laxmi-feature-card">
                  <div className="laxmi-feature-icon-wrapper">
                    {feature.icon}
                  </div>
                  <h3 className="laxmi-feature-title">{feature.title}</h3>
                  <p className="laxmi-feature-description">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Meals & Empathy Section */}
        <section className="laxmi-meals-section">
          <div className="laxmi-meals-grid">
            <div className="laxmi-empathy-card">
              <div className="laxmi-empathy-header">
                <Building size={48} className="laxmi-empathy-icon" />
                <h3 className="laxmi-empathy-title">Built for Industry, Backed by Experience</h3>
              </div>
              <p className="laxmi-empathy-text">
                We understand the complexities of staggered breaks, diverse shift patterns, and high-volume demands. Our response is flawless coordination combined with a people-first approach that prioritizes both efficiency and employee satisfaction.
              </p>
            </div>
            
            <div className="laxmi-meals-content">
              <h2 className="laxmi-section-title">Meals with Purpose</h2>
              <p className="laxmi-section-text">
                Our culinary programs extend far beyond generic meal provision. Each menu is meticulously customized to reflect regional taste preferences, specific nutritional requirements, and your team's daily operational rhythm.
              </p>
              <p className="laxmi-section-text">
                We create nourishing meal experiences that actively support focus, stamina, and workplace morale, transforming every canteen we manage into a clean, comfortable environment that employees genuinely appreciate and employers confidently rely upon.
              </p>
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="laxmi-philosophy-section">
          <div className="laxmi-philosophy-card">
            <h2 className="laxmi-philosophy-title">Our Professional Philosophy</h2>
            <p className="laxmi-philosophy-text">
              We embrace a customer-first mindset where traditional culinary excellence meets innovative service delivery, 
              crafting canteen experiences that are not only nutritionally comprehensive but also operationally memorable.
            </p>
            <p className="laxmi-philosophy-subtext">
              Whether optimizing cost efficiency, adapting to urgent service expansions, or implementing specialized dietary requirements, 
              our operational flexibility is matched only by our unwavering commitment to integrity and professional excellence.
            </p>
          </div>
        </section>

         {/* Highlights Section */}
        <section className="laxmi-highlights-section">
          <div className="laxmi-highlights-card">
            <h2 className="laxmi-highlights-title">What Sets Us Apart</h2>
            <div className="laxmi-highlights-grid">
              {laxmiHighlights.map((highlight, index) => (
                <div key={index} className="laxmi-highlight-item">
                  <CheckCircle size={24} className="laxmi-highlight-icon" />
                  <span className="laxmi-highlight-text">{highlight}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
        
      </main>
    </div>
  );
};

export default AboutUsSection;